/**
 * PPSP Banner v2 — bundled JS for this section only:
 * - subscription-form-common (Emarsys field helpers + traceId)
 * - bf-banner Alpine component
 * - countdown init
 *
 * Requires: Alpine.js (theme), theme.emailSubscription (when using the form),
 * optional window.getCookieForEmarsys
 * Pair with: ppsp-banner-v2.bundle.css
 */

// ========== subscription-form-common (subset used by banner) ==========
function getKeyValuePairWithSpecificFormat(jsonObject, format) {
  let result = {};
  for (let key in jsonObject) {
    if (key.indexOf(format) === 0) {
      result[key] = jsonObject[key];
    }
  }
  return result;
}

function massageJsonIntoArrayOfObjectsByPrefix(prefix, obj, type) {
  let result = {};

  for (let key in obj) {
    let numberPart = key.match(/\d+/)[0];

    if (!result[numberPart]) {
      result[numberPart] = {};
    }

    let newKey = key.replace(
      `${prefix ? prefix : ""}${type === "text" ? "subscription_text" : type}_` +
        `${numberPart}_`,
      ""
    );

    if (type === "multiple" && newKey === "value") {
      result[numberPart][newKey] = obj[key]
        .split(",")
        .map((item) => Number(item));
    } else if (type === "single" && newKey === "value") {
      result[numberPart][newKey] = Number(obj[key]);
    } else {
      result[numberPart][newKey] = obj[key];
    }

    if (newKey === "overwrite")
      result[numberPart]["overwrite"] = JSON.parse(obj[key]);
    result[numberPart]["type"] = type;
  }

  let arrayResult = Object.values(result);
  arrayResult = arrayResult.filter((item) => item.id !== "");
  return arrayResult;
}

function massageJsonIntoArrayOfObjects(obj, type) {
  return massageJsonIntoArrayOfObjectsByPrefix("", obj, type);
}

function massageSMSJsonIntoArrayOfObjects(obj, type) {
  return massageJsonIntoArrayOfObjectsByPrefix("sms_", obj, type);
}

function validateEmail(email) {
  var regex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
}

function validatePhone(code, phone) {
  const text = `${code.substring(1)}${phone}`;
  return /^\d{8,15}$/.test(text);
}

function createTraceId() {
  const date = new Date();
  const year = `00${date?.getFullYear()}`.slice(-2);
  const month = `00${date.getMonth() + 1}`.slice(-2);
  const day = `00${date.getDay()}`.slice(-2);
  const hour = `00${date.getHours()}`.slice(-2);
  const minute = `00${date.getMinutes()}`.slice(-2);
  const second = `00${date.getSeconds()}`.slice(-2);
  const millSecond = `000${date.getMilliseconds()}`.slice(-3);
  const randomStr1 = `${Math.random()}`;
  const randomStr2 = `${Math.random()}`;
  return (
    year +
    month +
    day +
    hour +
    minute +
    second +
    millSecond +
    randomStr1.slice(-8) +
    randomStr2.slice(-9)
  );
}

function getContactListId(json) {
  return json.contact_list_id;
}

function getSMSContactListId(json) {
  return json.sms_contact_list_id;
}

function createFieldsArray(json) {
  let getSingleObjects = getKeyValuePairWithSpecificFormat(json, "single_");
  let getTextObjects = getKeyValuePairWithSpecificFormat(
    json,
    "subscription_text_"
  );
  let getMultipleObjects = getKeyValuePairWithSpecificFormat(json, "multiple_");

  let singleObjectsArray = massageJsonIntoArrayOfObjects(
    getSingleObjects,
    "single"
  );
  let textObjectsArray = massageJsonIntoArrayOfObjects(getTextObjects, "text");
  let multipleObjectsArray = massageJsonIntoArrayOfObjects(
    getMultipleObjects,
    "multiple"
  );

  return [...singleObjectsArray, ...textObjectsArray, ...multipleObjectsArray];
}

function createSMSFieldsArray(json) {
  let getSingleObjects = getKeyValuePairWithSpecificFormat(json, "sms_single_");
  let getTextObjects = getKeyValuePairWithSpecificFormat(
    json,
    "sms_subscription_text_"
  );
  let getMultipleObjects = getKeyValuePairWithSpecificFormat(json, "sms_multiple_");

  let singleObjectsArray = massageSMSJsonIntoArrayOfObjects(
    getSingleObjects,
    "single"
  );
  let textObjectsArray = massageSMSJsonIntoArrayOfObjects(getTextObjects, "text");
  let multipleObjectsArray = massageSMSJsonIntoArrayOfObjects(
    getMultipleObjects,
    "multiple"
  );

  return [...singleObjectsArray, ...textObjectsArray, ...multipleObjectsArray];
}

window.traceId = createTraceId();

function isEmarsysSubscribeResponseSuccess(data) {
  if (!data || typeof data !== "object") return false;
  const raw = data.message ?? data.msg;
  if (typeof raw === "string" && raw.toLowerCase() === "success") return true;
  return false;
}

/** 接口错误文案（兼容 code 1006 等，message 可能在不同字段） */
function getEmarsysSubscribeApiErrorMessage(data) {
  if (!data || typeof data !== "object") return "";
  const nested = data.data && typeof data.data === "object" ? data.data : null;
  const m =
    data.message ??
    data.msg ??
    data.errorMessage ??
    (nested && (nested.message ?? nested.msg));
  if (typeof m === "string" && m.trim()) return m.trim();
  return "";
}

// ========== bf-banner-alpine ==========
document.addEventListener("alpine:init", () => {
  Alpine.data("bfBanner", () => ({
    loading: false,
    email: "",
    errMsg: "",
    disclaimerErrMsg: "",
    checked: true,
    isSuccess: false,

    async submitResult() {
      const emailErrorCopy =
        (bfbannerSettingJsons && bfbannerSettingJsons.email_error_text) ||
        "Please enter a valid email address";
      const disclaimerErrorCopy =
        (bfbannerSettingJsons && bfbannerSettingJsons.disclaimer_error_text) ||
        "Please agree to the disclaimer";

      if (!this.checked) {
        this.disclaimerErrMsg = disclaimerErrorCopy;
        return;
      } else {
        this.disclaimerErrMsg = "";
      }
      const isEmailValid = validateEmail(this.email);
      if (!isEmailValid) {
        this.errMsg = emailErrorCopy;
        return;
      }
      this.errMsg = "";

      if (!this.checked) {
        this.disclaimerErrMsg = disclaimerErrorCopy;
        return;
      }

      const fieldsArray = createFieldsArray(bfbannerSettingJsons);
      const contactListId = getContactListId(bfbannerSettingJsons);

      const { apiUrl, acceptedLanguage, apiKey, xAppid } =
        theme.emailSubscription;

      const subscribeInfoCookie = window.getCookieForEmarsys
        ? window.getCookieForEmarsys("subscribe_info")
        : null;
      let eventTracking = null;

      if (subscribeInfoCookie) {
        try {
          const decodedCookie = decodeURIComponent(subscribeInfoCookie);
          eventTracking = JSON.parse(decodedCookie);
          delete eventTracking.ts;
        } catch (error) {
          console.warn("Failed to parse subscribe_info cookie:", error);
        }
      }

      const apiBody = {
        email: this.email,
        contactListId,
        fields: fieldsArray,
        ...(eventTracking && { eventTracking }),
      };
      this.loading = true;
      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apiKey: apiKey,
            traceId: traceId,
            "X-appid": xAppid,
            "Accepted-Language": acceptedLanguage,
          },
          body: JSON.stringify(apiBody),
        });

        let data = {};
        try {
          const text = await response.text();
          data = text ? JSON.parse(text) : {};
        } catch (parseErr) {
          console.warn("Subscribe API: invalid JSON body", parseErr);
        }

        const apiErrText = getEmarsysSubscribeApiErrorMessage(data);
        const genericErr =
          "Oops... Something went wrong, please refresh and try again.";

        if (response.ok && isEmarsysSubscribeResponseSuccess(data)) {
          this.isSuccess = true;
          this.errMsg = "";
        } else if (!response.ok) {
          this.errMsg = apiErrText || genericErr;
        } else {
          // HTTP 2xx 但业务失败（如 code 1006、contactListId 为空）
          this.errMsg = apiErrText || genericErr;
        }
      } catch (error) {
        this.errMsg =
          "Oops... Something went wrong, please refresh and try again.";
      } finally {
        this.loading = false;
      }
    },
  }));
});

// ========== countdown.js ==========
function initCountdown(root) {
  if (!root || root.getAttribute("data-countdown-init") === "true") return;
  var endAttr = root.getAttribute("data-end");
  var durAttr = root.getAttribute("data-duration");
  var end = null;
  if (endAttr && endAttr.trim().length > 0) {
    var parsed = new Date(endAttr);
    if (!isNaN(parsed.getTime())) end = parsed.getTime();
  }
  if (!end && durAttr && !isNaN(Number(durAttr))) {
    end = Date.now() + Number(durAttr) * 1000;
  }
  if (!end) return;
  root.setAttribute("data-countdown-init", "true");
  var dEl = root.querySelector('.countdown__num[data-unit="days"]');
  var hEl = root.querySelector('.countdown__num[data-unit="hours"]');
  var mEl = root.querySelector('.countdown__num[data-unit="minutes"]');
  var sEl = root.querySelector('.countdown__num[data-unit="seconds"]');
  var padDays = !root.querySelector(".countdown__container__dpd");
  function pad(n) {
    return String(n).padStart(2, "0");
  }
  function tick() {
    var now = Date.now();
    var diff = Math.max(0, end - now);
    var totalSec = Math.floor(diff / 1000);
    var days = Math.floor(totalSec / 86400);
    var hours = Math.floor((totalSec % 86400) / 3600);
    var minutes = Math.floor((totalSec % 3600) / 60);
    var seconds = totalSec % 60;
    if (dEl) dEl.textContent = padDays ? pad(days) : String(days);
    if (hEl) hEl.textContent = pad(hours);
    if (mEl) mEl.textContent = pad(minutes);
    if (sEl) sEl.textContent = pad(seconds);
    if (diff <= 0) {
      clearInterval(timer);
      var ev = new CustomEvent("countdown:finished");
      root.dispatchEvent(ev);
      root.classList.add("hidden");
    }
  }
  tick();
  var timer = setInterval(tick, 1000);
}
function initCountdownAll(rootScope) {
  var scope = rootScope || document;
  scope.querySelectorAll(".countdown").forEach(function (node) {
    initCountdown(node);
  });
}
document.addEventListener("DOMContentLoaded", function () {
  initCountdownAll();
});
document.addEventListener("shopify:section:load", function (e) {
  initCountdownAll(e.target || document);
});
window.initCountdownAll = initCountdownAll;
